import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Wallet, Receipt, LogOut, Mail, Calendar, Coins, ArrowUpRight, ArrowDownLeft, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { fetchMyTransactions } from "@/lib/api";
import ApiKeysPanel from "@/components/ApiKeysPanel";

const REASON_LABELS = {
  paddle_payment: { tr: "Ödeme", en: "Payment" },
  welcome_bonus: { tr: "Hoşgeldin bonusu", en: "Welcome bonus" },
  api_usage: { tr: "API kullanımı", en: "API usage" },
};

export default function Profile() {
  const { user, loading, logout, refreshUser } = useAuth();
  const { lang } = useLang();
  const [txns, setTxns] = useState([]);
  const [txnsLoading, setTxnsLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setTxnsLoading(true);
    fetchMyTransactions()
      .then(setTxns)
      .finally(() => setTxnsLoading(false));
    refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (loading) {
    return (
      <div className="py-32 flex items-center justify-center" data-testid="profile-loading">
        <Loader2 className="animate-spin text-red-500" size={32} />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login?redirect=/profile" replace />;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="profile-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sidebar */}
        <aside className="lg:col-span-4">
          <div className="bg-neutral-950 border border-neutral-800 rounded-sm p-6">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-red-600 text-white headline text-2xl flex items-center justify-center rounded-sm uppercase">
                {(user.name || user.email)[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="headline text-xl text-white truncate" data-testid="profile-name">{user.name || user.email.split("@")[0]}</p>
                <p className="text-xs text-neutral-500 truncate font-mono">{user.email}</p>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-neutral-800">
              <p className="eyebrow text-neutral-500 mb-2">{lang === "tr" ? "PLAN" : "PLAN"}</p>
              <p className="headline text-2xl text-white capitalize" data-testid="profile-plan">{user.plan}</p>
            </div>

            <div className="mt-6 pt-6 border-t border-neutral-800 space-y-3">
              <div className="flex items-center gap-2 text-sm text-neutral-400">
                <Mail size={14} />
                <span className="truncate">{user.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-neutral-400">
                <Calendar size={14} />
                <span>{new Date(user.created_at).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US")}</span>
              </div>
            </div>

            <button
              onClick={logout}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-neutral-900 hover:bg-red-600 text-white py-2.5 rounded-sm uppercase tracking-wider font-bold text-xs transition-colors border border-neutral-800"
              data-testid="profile-logout"
            >
              <LogOut size={14} /> {lang === "tr" ? "Çıkış Yap" : "Sign Out"}
            </button>
          </div>
        </aside>

        {/* Main */}
        <div className="lg:col-span-8 space-y-6">
          {/* Credit balance */}
          <div className="relative overflow-hidden bg-gradient-to-br from-neutral-950 via-red-950/30 to-neutral-950 border-2 border-red-600 rounded-sm p-8">
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-red-600/10 blur-3xl" />
            <div className="relative flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Wallet size={18} className="text-red-500" />
                  <span className="eyebrow text-red-500">{lang === "tr" ? "API KULLANIM BAKİYESİ" : "API USAGE BALANCE"}</span>
                </div>
                <p className="headline text-6xl text-white mt-2" data-testid="profile-credits">
                  {user.credits.toLocaleString()}
                </p>
                <p className="text-sm text-neutral-400 mt-2">
                  {lang === "tr" ? "kullanım birimi · 1 API çağrısı = 1 birim" : "usage units · 1 API call = 1 unit"}
                </p>
                <p className="text-[10px] font-mono uppercase tracking-wider text-neutral-600 mt-2">
                  {lang === "tr" ? "Nakit değeri yoktur, devredilemez, yalnızca Loft Press API'sinde kullanılır." : "No cash value, non-transferable, usable only within Loft Press API."}
                </p>
              </div>
              <Link
                to="/pricing"
                className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors"
                data-testid="profile-topup-link"
              >
                {lang === "tr" ? "+ Birim Ekle" : "+ Top Up"}
              </Link>
            </div>
          </div>

          {/* API Keys */}
          <ApiKeysPanel userPlan={user.plan} />

          {/* Transactions */}
          <div className="bg-neutral-950 border border-neutral-800 rounded-sm">
            <div className="px-6 py-4 border-b border-neutral-800 flex items-center gap-2">
              <Receipt size={18} className="text-red-500" />
              <h2 className="headline text-xl text-white">{lang === "tr" ? "Hesap Hareketleri" : "Account Activity"}</h2>
            </div>
            {txnsLoading ? (
              <div className="py-12 flex items-center justify-center">
                <Loader2 className="animate-spin text-red-500" />
              </div>
            ) : txns.length === 0 ? (
              <div className="py-12 text-center text-neutral-500" data-testid="profile-no-txns">
                {lang === "tr" ? "Henüz hareket yok" : "No activity yet"}
              </div>
            ) : (
              <div className="divide-y divide-neutral-800">
                {txns.map((t, i) => {
                  const positive = t.delta > 0;
                  const reason = REASON_LABELS[t.reason]?.[lang] || t.reason;
                  return (
                    <div key={i} className="px-6 py-4 flex items-center gap-4" data-testid={`txn-${i}`}>
                      <div className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${
                        positive ? "bg-green-950/50 text-green-400 border border-green-900" : "bg-red-950/50 text-red-400 border border-red-900"
                      }`}>
                        {positive ? <ArrowDownLeft size={18} /> : <ArrowUpRight size={18} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-medium">{reason}</p>
                        <p className="text-xs text-neutral-500 font-mono">
                          {new Date(t.created_at).toLocaleString(lang === "tr" ? "tr-TR" : "en-US")}
                          {t.meta?.transaction_id && ` · ${t.meta.transaction_id.slice(0, 14)}...`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`headline text-xl ${positive ? "text-green-400" : "text-red-400"}`} data-testid={`txn-delta-${i}`}>
                          {positive ? "+" : ""}{t.delta.toLocaleString()}
                        </p>
                        <p className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">
                          {lang === "tr" ? "kalan" : "bal"} {t.balance_after.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
